import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TextField, Box, Popover } from '@mui/material';
import { styled } from '@mui/material/styles';

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

const TimePickerColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '2px',
  maxHeight: '180px',
  overflowY: 'auto',
  padding: '4px',
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none',
});

const TimePickerItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected',
})(({ selected }) => ({
  padding: '6px 12px',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: selected ? 700 : 400,
  color: selected ? '#fff' : '#666',
  backgroundColor: selected ? '#4AB3BC' : 'transparent',
  cursor: 'pointer',
  minWidth: '40px',
  textAlign: 'center',
  transition: 'all 0.1s',
  '&:hover': {
    backgroundColor: selected ? '#3EA0A8' : '#f0f0f0',
  },
}));

const CustomTimePicker = ({ value, onChange, disabled, sx }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);

  const scrollToSelected = useCallback(() => {
    setTimeout(() => {
      [hourRef, minuteRef].forEach(ref => {
        const el = ref.current;
        if (!el) return;
        const selected = el.querySelector('[data-selected="true"]');
        if (selected) {
          selected.scrollIntoView({ block: 'center', behavior: 'instant' });
        }
      });
    }, 50);
  }, []);

  useEffect(() => {
    if (anchorEl) scrollToSelected();
  }, [anchorEl, scrollToSelected]);

  // "HH:mm" → { period, hour, minute }
  const parsed = (() => {
    if (!value) return { period: '오전', hour: 12, minute: '00' };
    const [h, m] = value.split(':').map(Number);
    const period = h >= 12 ? '오후' : '오전';
    const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const minute = String(m).padStart(2, '0');
    return { period, hour, minute };
  })();

  const [period, setPeriod] = useState(parsed.period);
  const [hour, setHour] = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);

  useEffect(() => {
    setPeriod(parsed.period);
    setHour(parsed.hour);
    setMinute(parsed.minute);
  }, [value]);

  const buildTime = (p, h, m) => {
    let h24 = p === '오후' ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h);
    return `${String(h24).padStart(2, '0')}:${m}`;
  };

  const handleSelect = (type, val) => {
    let newP = period, newH = hour, newM = minute;
    if (type === 'period') { newP = val; setPeriod(val); }
    if (type === 'hour') { newH = val; setHour(val); }
    if (type === 'minute') { newM = val; setMinute(val); }
    onChange(buildTime(newP, newH, newM));
  };

  const displayValue = value
    ? `${parsed.period} ${parsed.hour}:${parsed.minute}`
    : '시간 선택';

  return (
    <>
      <TextField
        value={displayValue}
        onClick={e => !disabled && setAnchorEl(e.currentTarget)}
        size="small"
        inputProps={{ readOnly: true, style: { cursor: disabled ? 'default' : 'pointer' } }}
        disabled={disabled}
        sx={{
          // 130px 이면 옆의 날짜 칸이 좁아져 'YYYY-MM-DD' 뒷자리가 잘린다. 시분 표시엔 100px 로 충분하다.
          width: '100px',
          '& .MuiInputBase-root': {
            height: '44px',
            borderRadius: '4px',
            border: '1px solid #a6a6a6',
          },
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '& .MuiOutlinedInput-input': {
            padding: '12px 8px',
            fontSize: '14px',
            textAlign: 'center',
          },
          ...sx,
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            mt: '4px',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            backgroundColor: '#2a2a2a',
            p: '12px',
            display: 'flex',
            gap: '8px',
          },
        }}>
        <TimePickerColumn>
          {['오전', '오후'].map(p => (
            <TimePickerItem key={p} selected={period === p} onClick={() => handleSelect('period', p)}>
              {p}
            </TimePickerItem>
          ))}
        </TimePickerColumn>
        <TimePickerColumn ref={hourRef}>
          {HOURS.map(h => (
            <TimePickerItem key={h} selected={hour === h} data-selected={hour === h} onClick={() => handleSelect('hour', h)}>
              {h}
            </TimePickerItem>
          ))}
        </TimePickerColumn>
        <TimePickerColumn ref={minuteRef}>
          {MINUTES.map(m => (
            <TimePickerItem key={m} selected={minute === m} data-selected={minute === m} onClick={() => handleSelect('minute', m)}>
              {m}
            </TimePickerItem>
          ))}
        </TimePickerColumn>
      </Popover>
    </>
  );
};

export default CustomTimePicker;
